import type { CXAutomationFormState } from "../types";
import { appendQueryParams, parseRowValue, rowsToObject } from "./requestShape";

export interface TestRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

const TOKEN_PATTERN = /\{([^{}\s]+)\}/g;

export const substituteTokens = (
  input: string,
  tokenValues: Record<string, string>,
  variableNames: Set<string>
): string =>
  input.replace(TOKEN_PATTERN, (match, name: string) =>
    variableNames.has(name) ? tokenValues[name] ?? "" : match
  );

const substituteInRecord = (
  source: Record<string, string>,
  tokenValues: Record<string, string>,
  variableNames: Set<string>
): Record<string, string> => {
  const result: Record<string, string> = {};
  Object.entries(source).forEach(([key, value]) => {
    result[key] = substituteTokens(value, tokenValues, variableNames);
  });
  return result;
};

export const buildTestRequest = (
  state: CXAutomationFormState,
  tokenValues: Record<string, string>
): TestRequest => {
  const { method } = state;
  const isGet = method === "GET";
  const variableNames = new Set(
    state.dataInputs
      .map((input) => input.name.trim())
      .filter((name) => name.length > 0)
  );

  const headers = substituteInRecord(
    rowsToObject(state.requestRowsByTab.headers),
    tokenValues,
    variableNames
  );

  const rawUrl = appendQueryParams(
    state.urlMarkdown,
    state.requestRowsByTab.parameters
  );
  const url = substituteTokens(rawUrl, tokenValues, variableNames);

  const body: Record<string, unknown> = {};
  if (!isGet) {
    const substitutedBody = substituteInRecord(
      rowsToObject(state.requestRowsByTab.body),
      tokenValues,
      variableNames
    );
    Object.entries(substitutedBody).forEach(([key, value]) => {
      body[key] = parseRowValue(value);
    });
  }

  return { method, url, headers, body };
};
